// Phase 40 Plan 02 Task 4 — FirstSignInUploadModal.
// CONTEXT D-02, D-16. UI-SPEC §3.
//
// Brief blocking modal during the first-sign-in upload. Cannot be dismissed
// while uploading. Switches to an error state on failure with Try again /
// Not now actions. Plan 03 may extend the heading for the multi-device
// conflict path; Plan 02 ships the canonical heading + body lockstep with
// UI-SPEC.

import React, { useEffect, useRef } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';

export default function FirstSignInUploadModal({
  plantCount = 0,
  areaCount = 0,
  status = 'uploading', // 'uploading' | 'error'
  errorMessage = null,
  onRetry,
  onDismissError,
}) {
  const titleRef = useRef(null);
  useEffect(() => {
    titleRef.current?.focus();
    if (status === 'uploading') {
      // Block Escape so the user can't accidentally cancel an upload.
      const onKey = (e) => { if (e.key === 'Escape') e.preventDefault(); };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }
    return undefined;
  }, [status]);

  const showError = status === 'error';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'var(--c-overlay)', zIndex: 2100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="first-signin-modal-title"
    >
      <div
        style={{
          background: 'var(--c-bg)', borderRadius: 16, padding: 28,
          maxWidth: 320, width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
          textAlign: 'center', fontFamily: 'Lora, serif',
        }}
      >
        {showError ? (
          <>
            <AlertCircle size={32} stroke="var(--c-danger)" aria-hidden="true" style={{ margin: '0 auto 12px' }} />
            <h3
              id="first-signin-modal-title"
              ref={titleRef}
              tabIndex={-1}
              style={{
                fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600,
                color: 'var(--c-heading)', margin: '0 0 8px 0', outline: 'none',
              }}
            >
              Couldn't save to cloud.
            </h3>
            <p style={{ fontSize: '0.9375rem', color: 'var(--c-text)', lineHeight: 1.55, margin: '0 0 20px 0' }}>
              {errorMessage || 'Your data is safe locally. Try again.'}
            </p>
            <button
              type="button"
              onClick={onRetry}
              style={{
                background: 'var(--c-brand-deep)', color: '#fff', border: 'none',
                borderRadius: 24, padding: '12px 28px', fontSize: '0.9375rem', fontWeight: 700,
                cursor: 'pointer', width: '100%', minHeight: 44, marginBottom: 8,
                fontFamily: 'inherit',
              }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={onDismissError}
              style={{
                background: 'var(--c-btn-bg)', color: 'var(--c-btn-text)', border: 'none',
                borderRadius: 24, padding: '10px 22px', fontSize: '0.875rem', fontWeight: 600,
                cursor: 'pointer', minHeight: 44, fontFamily: 'inherit',
              }}
            >
              Not now
            </button>
          </>
        ) : (
          <>
            <Loader2
              size={32}
              stroke="var(--c-brand-deep)"
              aria-hidden="true"
              style={{ margin: '0 auto 16px', animation: 'tendSpin 1s linear infinite', display: 'block' }}
            />
            <h2
              id="first-signin-modal-title"
              ref={titleRef}
              tabIndex={-1}
              style={{
                fontFamily: "'Playfair Display', serif", fontSize: '1.25rem', fontWeight: 600,
                color: 'var(--c-heading)', margin: '0 0 8px 0', outline: 'none',
              }}
            >
              Saving your garden to the cloud
            </h2>
            <p style={{ fontSize: '0.9375rem', color: 'var(--c-text-2)', margin: 0, lineHeight: 1.55 }}>
              <em>{plantCount}</em> plants, <em>{areaCount}</em> areas
            </p>
            <style>{`
              @keyframes tendSpin { to { transform: rotate(360deg); } }
              @media (prefers-reduced-motion: reduce) {
                [aria-hidden="true"] { animation: none !important; }
              }
            `}</style>
          </>
        )}
      </div>
    </div>
  );
}
